/**
 * Design System Configuration
 *
 * Centralized spacing, typography, and layout constants
 * to ensure visual consistency across the admin portal.
 */

// ==================== SPACING SCALE ====================

/**
 * Spacing scale based on Ant Design tokens
 * Use these instead of hardcoded pixel values
 */
export const SPACING = {
  /** 4px - Minimal spacing */
  xs: 4,
  /** 8px - Small spacing between related items */
  sm: 8,
  /** 12px - Compact spacing */
  md: 12,
  /** 16px - Default spacing between items */
  base: 16,
  /** 24px - Spacing between sections */
  lg: 24,
  /** 32px - Large spacing between major sections */
  xl: 32,
  /** 48px - Extra large spacing */
  '2xl': 48,
} as const

// ==================== TYPOGRAPHY ====================

/**
 * Typography scale following Ant Design hierarchy
 */
export const TYPOGRAPHY = {
  size: {
    xs: 12,    // Metadata, timestamps
    sm: 13,    // Secondary text
    base: 14,  // Body text (Ant Design default)
    md: 15,    // Emphasized body
    lg: 16,    // Card titles (Title level 5)
    xl: 18,    // Section headers (Title level 4)
    '2xl': 20, // (Title level 4 emphasized)
    '3xl': 24, // Page sections (Title level 3)
    '4xl': 30, // Page title (Title level 2)
    '5xl': 38, // Dashboard title (Title level 1)
  },
  lineHeight: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.6,
  },
  weight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
} as const

// ==================== LAYOUT ====================

/**
 * Layout constants for consistent page structure
 */
export const LAYOUT = {
  /** Page container padding */
  page: {
    padding: `${SPACING.lg}px ${SPACING.xl}px`,
    paddingMobile: `${SPACING.base}px ${SPACING.base}px`,
  },
  /** Card margins */
  card: {
    marginBottom: SPACING.lg,
  },
  /** Grid gutter */
  grid: {
    gutter: [SPACING.base, SPACING.base] as [number, number],
    gutterLarge: [SPACING.lg, SPACING.lg] as [number, number],
  },
  /** Maximum content width */
  maxWidth: {
    content: 1200,
    narrow: 800,
    wide: 1400,
  },
} as const

// ==================== BORDER RADIUS ====================

export const BORDER_RADIUS = {
  sm: 4,
  base: 6, // Ant Design theme default
  md: 8,
  lg: 12,
} as const

// ==================== Z-INDEX ====================

export const Z_INDEX = {
  dropdown: 1000,
  modal: 1050,
  popover: 1060,
  tooltip: 1070,
} as const

// ==================== BREAKPOINTS ====================

/**
 * Responsive breakpoints (matches Ant Design)
 */
export const BREAKPOINTS = {
  xs: 480,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1600,
} as const

// ==================== UTILITY FUNCTIONS ====================

/**
 * Create consistent margin bottom style
 */
export function marginBottom(size: keyof typeof SPACING) {
  return { marginBottom: SPACING[size] }
}

/**
 * Create consistent padding style
 */
export function padding(size: keyof typeof SPACING) {
  return { padding: SPACING[size] }
}

/**
 * Create consistent gap for flex/grid
 */
export function gap(size: keyof typeof SPACING) {
  return { gap: SPACING[size] }
}
